import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Send, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminPushNotifications() {
  const [stats, setStats] = useState({ total: 0, with_user: 0, cities: [] });
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", url: "/", city: "", icon: "/pwa-icon.svg" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const loadStats = async () => {
    try { const { data } = await api.get("/admin/push/stats"); setStats(data || stats); } catch (e) { console.debug("push stats load failed", e); }
  };
  useEffect(() => {
    loadStats();
    api.get("/cities").then((r) => setCities(r.data || []));
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) { toast.error("Titre et message obligatoires."); return; }
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/admin/push/broadcast", form);
      setResult(data);
      toast.success(`Envoyé à ${data.sent} abonné${data.sent > 1 ? "s" : ""}${data.failed ? ` · ${data.failed} échec${data.failed > 1 ? "s" : ""}` : ""}`);
      await loadStats();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur."); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-push-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Bell className="text-[#E05638]" size={26}/> Notifications push
          </h1>
          <p className="text-slate-500 text-xs mt-1">Envoyez une notification à tous les abonnés (filtrable par commune).</p>
        </div>
        <Button variant="outline" onClick={loadStats} data-testid="admin-push-reload">
          <RefreshCw size={14} className="mr-1"/> Actualiser
        </Button>
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-push-stat-total">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Abonnés totaux</div>
          <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="admin-push-stat-users">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Connectés</div>
          <div className="font-heading text-2xl font-extrabold text-emerald-600 mt-1">{stats.with_user}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 col-span-2 md:col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Top communes abonnées</div>
          {stats.cities?.length ? (
            <div className="flex flex-wrap gap-1">
              {stats.cities.slice(0, 8).map((c) => (
                <span key={c.city} className="text-xs bg-slate-100 rounded-full px-2 py-0.5">
                  {c.city} <b>{c.count}</b>
                </span>
              ))}
            </div>
          ) : <div className="text-xs text-slate-400">Aucun filtre commune utilisé pour l'instant.</div>}
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <div>
          <Label>Titre *</Label>
          <Input value={form.title} maxLength={120} onChange={(e) => set("title", e.target.value)}
                 placeholder="Ex: Nouveau bon plan à Chartres !" required data-testid="admin-push-title"/>
        </div>
        <div>
          <Label>Message *</Label>
          <Textarea rows={3} maxLength={300} value={form.body} onChange={(e) => set("body", e.target.value)}
                    placeholder="Ex: -30% chez La Boulangerie du Centre, valable jusqu'à dimanche."
                    required data-testid="admin-push-body"/>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>URL de destination (facultatif)</Label>
            <Input value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="/offre/123 ou /bons-plans"
                   data-testid="admin-push-url"/>
          </div>
          <div>
            <Label>Cibler une commune (facultatif)</Label>
            <Select value={form.city} onValueChange={(v) => set("city", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="admin-push-city"><SelectValue placeholder="Toutes les communes"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les communes</SelectItem>
                {cities.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={sending || stats.total === 0} className="btn-shine text-white h-11 px-6"
                  data-testid="admin-push-send">
            {sending ? <><Loader2 size={14} className="mr-1 animate-spin"/> Envoi...</> : <><Send size={14} className="mr-1"/> Envoyer la notification</>}
          </Button>
        </div>
        {stats.total === 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Aucun abonné pour l'instant. Les utilisateurs doivent cliquer sur « Activer les notifications » depuis leur compte ou la Home.
          </p>
        )}
        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-900" data-testid="admin-push-result">
            <b>Résultat :</b> envoyé {result.sent} · échecs {result.failed} · expirés nettoyés {result.pruned}
          </div>
        )}
      </form>
    </div>
  );
}
